/* components/patterns/Sidebar.jsx — единый сайдбар рабочего пространства (S5, ревизия).
   Сверху: WorkspaceSwitcher + лимит сообщений (UsageMeter). Затем основная навигация.
   Снизу (растёт+скролл): список диалогов (поиск, «+», группы Личные/Общие, контекст-меню,
   inline-rename). В самом низу: UserMenu.
   Список диалогов питается из общего ChatProvider (useChat) — один источник правды
   с экраном ChatShell. Один комплексный сайдбар вместо двух панелей (решение пользователя).
   container-query управляет свёрнутым видом (см. breakpoints.css). */

import React from 'react';
import { navConfig } from '../../app/navConfig.js';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { useChat } from '../../app/providers/ChatProvider.jsx';
import { NavItem } from './NavLink.jsx';
import { UserMenu } from './UserMenu.jsx';
import { UsageMeter, WorkspaceSwitcher } from './WorkspaceSwitcher.jsx';
import { Button } from '../ui/Button.jsx';
import { SearchInput } from '../ui/Field.jsx';
import { Icon } from '../ui/Icon.jsx';
import { Popover, PopoverItem } from '../ui/Popover.jsx';
import { RR } from '../../lib/router.jsx';
import { canUseAssistant } from '../../lib/assistantAccess.js';

  const { useState, useEffect, useRef, useMemo } = React;
  // isNavItemVisible — ЕДИНОЕ правило видимости пункта (navConfig.js): consoleOnly
  // плюс рубильники разделов из /api/auth/me. Сайдбар фильтровал только consoleOnly,
  // поэтому «Обучение» оставалось в меню при выключенном ACADEMY_ENABLED и вело на
  // экран «раздел отключён» — пункт обещал функцию, которой нет (PL-7).
  // Третий аргумент — активное пространство из useAuth(): тариф может выключить целый
  // раздел («Аналитика») для всех участников; смена пространства (switchWorkspace) и
  // refreshWorkspaces обновляют activeWorkspace, меню перерисовывается сразу.
  const { PRIMARY_NAV, isNavItemVisible } = navConfig;
  const { useNavigate, useLocation } = RR;

  /* строка диалога */
  function ChatRow({ chat, active, canManage, onOpen, onRename, onShareToggle, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(chat.title);
    const inputRef = useRef(null);
    useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);
    const commit = () => { const v = draft.trim(); if (v && v !== chat.title) onRename(chat, v); setEditing(false); };
    const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else if (e.key === 'Escape') { setDraft(chat.title); setEditing(false); } };
    const shared = chat.visibility === 'workspace';
    const spawnParent = chat.metadata && chat.metadata.spawn_for_title;
    // #503: пришёл ответ, пока человек был не здесь — точка и полужирный заголовок; в момент
    // прихода (fresh) — короткая вспышка строки и пульс точки
    const unread = !!chat.has_new_reply && !active;
    return (
      <div className={'chat-item' + (active ? ' is-active' : '') + (spawnParent ? ' chat-item--child' : '')
        + (unread ? ' has-new' : '') + (unread && chat.fresh ? ' is-fresh' : '')}>
        {editing ? (
          <input ref={inputRef} className="chat-item__rename input" value={draft}
                 onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={onKey} aria-label="Название" />
        ) : (
          <button className="chat-item__btn" onClick={() => onOpen(chat)}>
            <span className="chat-item__texts">
              <span className="chat-item__title truncate">{chat.title}</span>
              {spawnParent && <span className="chat-item__parent truncate t-caption text-mute">↳ {spawnParent}</span>}
            </span>
            {unread && <span className="chat-item__new" aria-hidden="true" />}
            {unread && <span className="visually-hidden">— новый ответ</span>}
          </button>
        )}
        {!editing && (
          <Popover placement="bottom-end" width={210} trigger={
            <button className="chat-item__kebab" aria-label="Меню чата"><Icon name="more-vertical" size={16} /></button>
          }>
            {({ close }) => (
              <div className="stack stack-2">
                <PopoverItem icon="edit" close={close} onClick={() => setEditing(true)}>Переименовать</PopoverItem>
                {canManage && (
                  <PopoverItem icon="share" close={close} onClick={() => onShareToggle(chat, !shared)}>
                    {shared ? 'Вернуть в личные' : 'Вывести в общие'}
                  </PopoverItem>
                )}
                {chat.can_manage && <>
                  <div className="popover__sep" />
                  <PopoverItem icon="trash" danger close={close} onClick={() => onDelete(chat)}>Удалить</PopoverItem>
                </>}
              </div>
            )}
          </Popover>
        )}
      </div>
    );
  }

  function ChatGroup({ title, chats, activeId, ...rest }) {
    if (!chats.length) return null;
    return (
      <div className="chat-group">
        {title && <div className="sidebar__section-title">{title}</div>}
        {chats.map((c) => <ChatRow key={c.id} chat={c} active={c.id === activeId} {...rest} />)}
      </div>
    );
  }

  // onClose (опц.) — рендерит крестик закрытия в шапке (полноэкранный список диалогов на xs)
  function SidebarChats({ onNavigate, onClose }) {
    const { activeWorkspace } = useAuth();
    const chat = useChat();
    const location = useLocation();
    const navigate = useNavigate();
    // shell ожидает разрешённый auth; при async-API activeWorkspace может быть null до загрузки
    const isBusiness = activeWorkspace?.kind === 'business';
    const m = (location.pathname || '').match(/^\/chat\/(.+)$/);
    const activeId = m ? m[1] : null;

    const filtered = useMemo(() => {
      const q = (chat.search || '').trim().toLowerCase();
      return (chat.chats || []).filter((c) => !q || c.title.toLowerCase().includes(q));
    }, [chat.chats, chat.search]);
    const mine = filtered.filter((c) => c.visibility === 'private');
    const shared = filtered.filter((c) => c.visibility === 'workspace');

    const open = (c) => { chat.openChat(c); onNavigate && onNavigate(); };
    const create = () => { chat.newChat(); onNavigate && onNavigate(); };
    const rowProps = { activeId, canManage: isBusiness, onOpen: open, onRename: chat.renameChat,
      onShareToggle: chat.shareChat, onDelete: (c) => chat.deleteChat(c, activeId) };

    // переключатель Личные/Общие (только Business); вкладка живёт в ChatProvider —
    // shareChat переключает её вслед за перенесённым чатом («В общие»/«В личные»)
    const tab = chat.listTab || 'private';
    const setTab = chat.setListTab;
    const visibleList = tab === 'private' ? mine : shared;
    // если выбранная вкладка пуста при активном поиске — мягкий fallback к непустой
    const tabCount = { private: mine.length, workspace: shared.length };
    // #503: новый ответ в диалоге на другой вкладке — точка у её названия
    const tabNew = { private: mine.some((c) => c.has_new_reply && c.id !== activeId),
                     workspace: shared.some((c) => c.has_new_reply && c.id !== activeId) };

    return (
      <div className="sidebar-chats">
        <div className="sidebar-chats__head">
          <span className="sidebar-chats__head-left">
            {onClose && (
              <button className="chat-header__icon" onClick={onClose} aria-label="Закрыть">
                <Icon name="close" size={20} />
              </button>
            )}
            <span className="sidebar__section-title sidebar-chats__title">Диалоги</span>
          </span>
          <button className="sidebar-chats__new" onClick={create} aria-label="Новый чат"><Icon name="plus" size={14} /></button>
        </div>
        <div className="sidebar-chats__search">
          <SearchInput value={chat.search} onChange={chat.setSearch} placeholder="Поиск…" />
        </div>
        {isBusiness && !chat.loading && !chat.error && !chat.forbidden && (
          <div className="sidebar-chats__seg" role="tablist" aria-label="Видимость чатов">
            <div className="seg">
              <button type="button" role="tab" aria-selected={tab === 'private'}
                className={'seg__btn' + (tab === 'private' ? ' is-active' : '')}
                onClick={() => setTab('private')}>
                Личные{tabCount.private ? ` · ${tabCount.private}` : ''}
                {tabNew.private && tab !== 'private' && <><span className="seg__new" aria-hidden="true" /><span className="visually-hidden"> — есть новый ответ</span></>}
              </button>
              <button type="button" role="tab" aria-selected={tab === 'workspace'}
                className={'seg__btn' + (tab === 'workspace' ? ' is-active' : '')}
                onClick={() => setTab('workspace')}>
                Общие{tabCount.workspace ? ` · ${tabCount.workspace}` : ''}
                {tabNew.workspace && tab !== 'workspace' && <><span className="seg__new" aria-hidden="true" /><span className="visually-hidden"> — есть новый ответ</span></>}
              </button>
            </div>
          </div>
        )}
        <div className="sidebar-chats__list">
          {chat.loading && (
            <div className="stack stack-2" style={{ padding: '2px' }}>
              {[0,1,2,3].map((i) => <div key={i} className="skeleton" style={{ height: 30, borderRadius: 8 }} />)}
            </div>
          )}
          {!chat.loading && chat.forbidden && <div className="chat-list__state t-caption text-dim">Нет доступа к диалогам.</div>}
          {!chat.loading && chat.error && !chat.forbidden && (
            <div className="chat-list__state">
              <span className="t-caption text-danger">Не удалось загрузить</span>
              <button className="link-btn t-caption" onClick={chat.reload}>Повторить</button>
            </div>
          )}
          {!chat.loading && !chat.error && !chat.forbidden && filtered.length === 0 && (
            <div className="chat-list__state stack stack-6">
              {chat.search
                ? <span className="t-caption text-dim">Ничего не нашли.</span>
                : <span className="t-caption text-dim">Пока нет диалогов.</span>}
            </div>
          )}
          {!chat.loading && !chat.error && !chat.forbidden && filtered.length > 0 && (
            isBusiness
              ? (visibleList.length > 0
                  ? <ChatGroup title={null} chats={visibleList} {...rowProps} />
                  : <div className="chat-list__state t-caption text-dim">
                      {tab === 'private' ? 'Нет личных диалогов.' : 'Нет общих диалогов.'}
                    </div>)
              : <ChatGroup title={null} chats={filtered} {...rowProps} />
          )}
        </div>
      </div>
    );
  }

  // скелет сайдбара, пока auth не разрешён (shell ожидает разрешённый auth — фикс P3-22)
  function SidebarSkeleton() {
    return (
      <div className="sidebar__inner" aria-busy="true" aria-label="Загрузка навигации">
        <div className="sidebar__top stack stack-6">
          <div className="skeleton" style={{ height: 42, borderRadius: 8 }} />
        </div>
        <nav className="sidebar__nav stack stack-2">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="skeleton" style={{ height: 36, borderRadius: 8 }} />)}
        </nav>
        <div className="sidebar__chats grow" />
        <div className="sidebar__bottom">
          <div className="skeleton" style={{ height: 40, borderRadius: 8 }} />
        </div>
      </div>
    );
  }

  function Sidebar({ onNavigate }) {
    const { user, activeWorkspace } = useAuth();
    const handleNav = () => { onNavigate && onNavigate(); };
    // #503: диалоги с новым ответом — счётчик на пункте «Ассистент»
    const chatCtx = useChat();
    const unread = (chatCtx && chatCtx.unreadCount) || 0;

    // пока auth не разрешён (нет пользователя/пространства) — скелет вместо краша вложенных меню
    if (!user || !activeWorkspace) return <SidebarSkeleton />;

    return (
      <div className="sidebar__inner">
        <div className="sidebar__top stack stack-6">
          <WorkspaceSwitcher />
        </div>

        <nav className="sidebar__nav stack stack-2" aria-label="Основная навигация">
          {PRIMARY_NAV.filter((item) => isNavItemVisible(item, user, activeWorkspace)).map((item) => (
            <NavItem key={item.id} to={item.to} icon={item.icon} label={item.label} brand={item.brand} onClick={handleNav} data-onb={'nav-' + item.id}
              {...(item.id === 'chat' ? { badge: unread, badgeTone: 'accent', badgeLabel: `Диалогов с новым ответом: ${unread}` } : {})} />
          ))}
        </nav>

        {/* список диалогов — снизу, растёт и скроллится; Аргус закрыт участнику (№523) — блока
            нет: «Новый чат» завёл бы чат, который откроется экраном отказа */}
        <div className="sidebar__chats grow">
          {canUseAssistant(activeWorkspace) && <SidebarChats onNavigate={onNavigate} />}
        </div>

        <div className="sidebar__bottom">
          <UserMenu />
        </div>
      </div>
    );
  }

export { Sidebar };
export { SidebarChats };
